import { PricingGroupBy } from '@/domain/pricing/types';

export { PricingGroupBy };

const GROUP_BY_VALUES = new Set<string>(Object.values(PricingGroupBy));

export const isPricingGroupBy = (value: unknown): value is PricingGroupBy =>
  typeof value === 'string' && GROUP_BY_VALUES.has(value);

/** Leaf rows are labeled by whichever dimension the parent is not. */
const LEAF_DIMENSION: Record<PricingGroupBy, PricingGroupBy> = {
  [PricingGroupBy.Restaurant]: PricingGroupBy.VariantPack,
  [PricingGroupBy.VariantPack]: PricingGroupBy.Restaurant,
};

export const leafDimensionOf = (groupBy: PricingGroupBy): PricingGroupBy =>
  LEAF_DIMENSION[groupBy];

type StringKeys<T> = {
  [K in keyof T]-?: T[K] extends string ? K : never;
}[keyof T];

/** Which fields of a row hold one dimension's id, uuid and display name. */
export interface PricingLabelFields<T> {
  id: StringKeys<T>;
  key: StringKeys<T>;
  name: StringKeys<T>;
}

export interface PricingLabel {
  /** External id shown in the Id column (item code or customer account id). */
  id: string;
  /** UUID of the entity. */
  key: string;
  name: string;
}

export const pickLabel = <T>(
  row: T,
  fields: PricingLabelFields<T>,
): PricingLabel => ({
  id: row[fields.id] as string,
  key: row[fields.key] as string,
  name: row[fields.name] as string,
});

/** What a parent row stands for, independent of the grouping in effect. */
export interface PricingGroupIdentity {
  groupBy: PricingGroupBy;
  groupId: string;
  /** UUID of the grouping entity; unique across parent rows. */
  groupKey: string;
  groupName: string;
}

export const toGroupIdentity = (
  groupBy: PricingGroupBy,
  { id, key, name }: PricingLabel,
): PricingGroupIdentity => ({
  groupBy,
  groupId: id,
  groupKey: key,
  groupName: name,
});

interface GroupableRow {
  restaurantUUID: string;
  variantPackUUID: string;
}

const GROUP_KEY_FIELD: Record<PricingGroupBy, keyof GroupableRow> = {
  [PricingGroupBy.Restaurant]: 'restaurantUUID',
  [PricingGroupBy.VariantPack]: 'variantPackUUID',
};

export const groupKeyOf = (
  row: GroupableRow,
  groupBy: PricingGroupBy,
): string => row[GROUP_KEY_FIELD[groupBy]];

interface LabeledAccountRow extends GroupableRow {
  restaurantAccountId: string;
  restaurantName: string;
  variantPackId: string;
  variantPackName: string;
}

const ACCOUNT_LABEL_FIELDS: Record<
  PricingGroupBy,
  PricingLabelFields<LabeledAccountRow>
> = {
  [PricingGroupBy.Restaurant]: {
    id: 'restaurantAccountId',
    key: 'restaurantUUID',
    name: 'restaurantName',
  },
  [PricingGroupBy.VariantPack]: {
    id: 'variantPackId',
    key: 'variantPackUUID',
    name: 'variantPackName',
  },
};

export const groupIdentityOf = (
  row: LabeledAccountRow,
  groupBy: PricingGroupBy,
): PricingGroupIdentity =>
  toGroupIdentity(groupBy, pickLabel(row, ACCOUNT_LABEL_FIELDS[groupBy]));

export const leafLabelOf = (
  row: LabeledAccountRow,
  groupBy: PricingGroupBy,
): PricingLabel =>
  pickLabel(row, ACCOUNT_LABEL_FIELDS[leafDimensionOf(groupBy)]);
